import React, { useState, useMemo } from 'react'
import { useStore } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'antd';
import Tooltip from '../common/Tooltip';
import KeepTooltip from '../common/KeepTooltip';
import ButtonTableNavbar from './PopMenu';
import SelectTableRows from './SelectTableRows';
import { GroupOutlined, InfoCircleOutlined, MergeCellsOutlined, SplitCellsOutlined, UngroupOutlined } from '@ant-design/icons';
import TableStyleSelect from './TableStyleSelect';
import TableCascader from './TableCascader';
import VariableSwitcher from '../common/VariableSwitcher';
import HideInFormSwitcher from '../common/HideInFormSwitcher';
import BoxFormControlsPortal, { getSectionAccentColor } from '../RightSide/BoxFormControlsPortal';
import { underlinedPanelHeadingStyle } from '../../shared/ui/panelHeading';
import { defaultTableConfig, isConfigurableTable } from '../../constants/constants';
import { getSectionRecursiveMeta } from '../../utils/recursiveSections';
import { alignTableColumn } from '../../utils/table';

const selector = (state) => {
  return {
    currentDocument: state.currentDocument,
    updateBox: state.updateBox,
    isAdmin: state.user?.isAdmin,
    handleSelectBox: state.handleSelectBox,
    splittableTables: state.splittableTables,
    zoneSplitTables: state.zoneSplitTables,
    openTableConfigDrawer: state.openTableConfigDrawer,
    applyTableChangeInStore: state.applyTableChange,
    setTableColumnAlign: state.setTableColumnAlign,
    sections: state.sections,
  };
};


const fieldLabelStyle = {
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(11, 34, 57, 0.65)',
  whiteSpace: 'nowrap',
};

// Sous-groupe d'une section du panel : titre de section, un cran plus petit.
const subgroupLabelStyle = (color) => ({
  ...underlinedPanelHeadingStyle(color),
  fontSize: 11,
  width: 'fit-content',
});

// Légende de la ligne-titre sélectionnée : une ligne, tronquée sans élargir
// le panneau (libellé complet au survol).
const labelRowCaptionStyle = {
  fontSize: 12,
  color: 'rgba(11, 34, 57, 0.6)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Pastille d'aide à côté d'un titre de section. Remontée de la hauteur du
// souligné du titre (padding 4 + trait 2) pour se centrer sur son texte.
const helpIconStyle = {
  color: 'rgba(11, 34, 57, 0.45)',
  cursor: 'help',
  fontSize: 13,
  marginBottom: 6,
};

// Valeurs des boutons → valeurs stockées pour un titre (cf. Header.alignHeaderText).
const headerTextAlign = (value) => ({ left: 'start', right: 'end' }[value] ?? value);

const NavbarTable = ({
  selectedBox,
  controlRowsClicked,
  setControlRowsClicked,
  selectedItem,
  canMergeCells,
  canUnmergeCells,
  onMergeCells,
  onUnmergeCells,
  selectedLabelRow,
  onToggleLabelRowMerge,
  canGroupColumns,
  canUngroupColumns,
  onGroupColumns,
  onUngroupColumns,
}) => {
  const boxId = selectedBox?.id;
  const isCustomTable = !selectedBox?.tableName;
  // Un tableau variable ne fusionne que ses lignes ajoutées à la main : la
  // section n'a d'intérêt que s'il en a.
  const hasManualRows = !isCustomTable && !!selectedBox?.content?.data?.some((row) => row.manual);
  const [pendingSplitChoice, setPendingSplitChoice] = useState(null);
  const [isApplyingSplitChoice, setIsApplyingSplitChoice] = useState(false);
  const {
    currentDocument,
    updateBox,
    isAdmin,
    splittableTables,
    zoneSplitTables,
    openTableConfigDrawer,
    applyTableChangeInStore,
    setTableColumnAlign,
    sections,
  } = useStore(selector);
  const { t } = useTranslation();
  // Comment fusionner : seule explication du bouton grisé, et du geste (Maj +
  // clic) que rien d'autre n'annonce dans le document.
  const canMerge = isCustomTable || hasManualRows;
  const mergeHint = t(isCustomTable ? 'table-merge-cells-hint' : 'table-merge-manual-rows-hint');

  const isBoxInRecursiveSection = useMemo(() => {
    if (!selectedBox?.fromSectionId) return false;
    const section = sections?.find(s => String(s.id) === String(selectedBox.fromSectionId));
    return getSectionRecursiveMeta(section).isRecursive;
  }, [sections, selectedBox?.fromSectionId]);
  // Colonnes d'un tableau lu de l'API : leur alignement va aussi dans la config,
  // seule à survivre à une relecture de la table.
  const persistColumnsAlign = (field, value) => {
    if (isCustomTable) return;
    controlRowsClicked
      .filter(({ parentRowType }) => parentRowType === 'header')
      .forEach(({ parentRowId }) => setTableColumnAlign(boxId, parentRowId, field, value));
  };
  const handleNavbarClick = ({ action, value }) => {
    if (action === 'alignment') persistColumnsAlign('align', value);
    // Tableau lu de l'API : la config seule survit à une relecture.
    if (action === 'alignment' && !isCustomTable && controlRowsClicked.length === 0 && selectedItem?.type === 'columns') {
      setTableColumnAlign(boxId, selectedItem.columnKey, 'headerAlign', headerTextAlign(value));
    }
    if (action === 'verticalAlignment') persistColumnsAlign('verticalAlign', value);
    switch (action) {
      case 'alignment':
        updateBox(boxId, (box) => {
          if (controlRowsClicked.length > 0) {
            // Alignement appliqué à la sélection de colonnes ou de lignes
            controlRowsClicked.forEach(({ parentRowId, parentRowType }) => {
              if (parentRowType === 'header') {
                alignTableColumn(box.content, parentRowId, 'textAlign', value);
              } else if (parentRowType === 'cell') {
                const targetedElement = box.content.data.find((c) => c.key === parentRowId);
                if (targetedElement) {
                  box.content.columns.forEach((column) => {
                    targetedElement[`textAlign_${column.key}`] = value;
                  });
                }
              }
            });
          } else if (selectedItem?.type === 'data') {
            // Alignement appliqué uniquement à la cellule ayant le focus
            const { rowKey, columnKey } = selectedItem;
            const targetedRow = box.content.data.find((row) => row.key === rowKey);
            if (targetedRow) {
              targetedRow[`textAlign_${columnKey}`] = value;
            }
          } else if (selectedItem?.type === 'columns') {
            // En-tête ayant le focus : son seul titre, comme la barre flottante.
            const column = box.content.columns.find((c) => c.key === selectedItem.columnKey);
            if (column) column.headerTextAlign = headerTextAlign(value);
          }
          // if (controlRowClicked.parentRowType === 'header') {
          //   // Pour les en-têtes, mettre à jour la colonne
          //   const targetedElement = box.content.columns.find((c) => c.key === key);
          //   if (targetedElement) {
          //     targetedElement.textAlign = value;
          //     box.content.data.forEach((row) => {
          //       row.textAlign = value;
          //     });
          //   }
          // } else if (controlRowClicked.parentRowType === 'cell') {
          //   // Pour les cellules, mettre à jour la colonne correspondante
          //   const targetedElement = box.content.data.find((c) => c.key === key);
          //   if (targetedElement) {
          //     targetedElement.textAlign = value;
          //   }
          // }
        });
        break;
      case 'verticalAlignment':
        updateBox(boxId, (box) => {
          if (controlRowsClicked.length > 0) {
            // Alignement vertical appliqué à la sélection de colonnes ou de lignes
            controlRowsClicked.forEach(({ parentRowId, parentRowType }) => {
              if (parentRowType === 'header') {
                alignTableColumn(box.content, parentRowId, 'verticalAlign', value);
              } else if (parentRowType === 'cell') {
                const targetedRow = box.content.data.find((row) => row.key === parentRowId);
                if (targetedRow) {
                  box.content.columns.forEach((column) => {
                    targetedRow[`verticalAlign_${column.key}`] = value;
                  });
                }
              }
            });
          } else if (selectedItem?.type === 'data') {
            // Alignement vertical appliqué uniquement à la cellule ayant le focus
            const { rowKey, columnKey } = selectedItem;
            const targetedRow = box.content.data.find((row) => row.key === rowKey);
            if (targetedRow) {
              targetedRow[`verticalAlign_${columnKey}`] = value;
            }
          }
        });
        break;
      default:
        break;
    }
  };

  // Tout tableau s'ouvre sur sa configuration, une fois appliqué.
  const applyTableChange = async (tableName, proposal, { split = false } = {}) => {
    await applyTableChangeInStore(selectedBox, tableName, proposal, { split });
    openTableConfigDrawer(selectedBox.id);
  };

  const handleChangeTable = async (tableName, proposal) => {
    const configurable = isConfigurableTable(tableName);
    const zoneSplit = !!zoneSplitTables?.includes(tableName);
    if (isAdmin && currentDocument.type === 'template') {
      updateBox(selectedBox.id, (boxToUpdate) => {
        boxToUpdate.tableName = tableName;
        boxToUpdate.content.columns = [];
        boxToUpdate.content.data = [];
        boxToUpdate.proposal = proposal;
        boxToUpdate.tableConfig = configurable ? defaultTableConfig(tableName) : undefined;
      });
      openTableConfigDrawer(selectedBox.id);
    } else {
      const isSplittableTable = Array.isArray(splittableTables) && splittableTables.some((t) => {
        if (typeof t === 'string') return t === tableName;
        return t?.tableName === tableName || t?.table_name === tableName;
      });

      // Un tableau découpable par zone en règle le découpage dans le Drawer :
      // pas de question à la sélection.
      if (isSplittableTable && !zoneSplit && !isBoxInRecursiveSection) {
        setPendingSplitChoice({ tableName, proposal });
        return;
      }

      await applyTableChange(tableName, proposal, { split: false });
    }
  };

  return (
    <div
      data-outside-ignore="true"
      onMouseDown={(e) => {
        // Empêche la désélection du tableau quand on interagit avec la navbar
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 14,
        width: '100%',
      }}
    >
      <ButtonTableNavbar
        onClick={handleNavbarClick}
        type={controlRowsClicked.length > 0 ? controlRowsClicked[0].parentRowType : 'cell'}
      />
      <SelectTableRows selectedBoxId={boxId} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={fieldLabelStyle}>{t('table-style')}</span>
        <TableStyleSelect boxId={boxId} />
      </div>
      {(isCustomTable || hasManualRows || selectedLabelRow) && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          paddingTop: 12,
          borderTop: '1px solid rgba(11, 34, 57, 0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={subgroupLabelStyle(getSectionAccentColor(t('cells')))}>{t('cells')}</span>
            {/* Les infobulles sont coupées dans le panneau (cf. TooltipsContext),
                KeepTooltip rallume celles de la fusion : sans elles, rien
                n'explique pourquoi « Fusionner » reste grisé. */}
            {canMerge && (
              <KeepTooltip>
                <Tooltip title={mergeHint} placement="left">
                  <InfoCircleOutlined tabIndex={0} aria-label={mergeHint} style={helpIconStyle} />
                </Tooltip>
              </KeepTooltip>
            )}
          </div>
          {/* Fusion libre : tables custom, et lignes ajoutées à la main d'un
              tableau variable. */}
          {canMerge && (
            <>
              {/* Toujours présent ; désactivé + tooltip tant qu'aucune plage ≥2 cases. */}
              <KeepTooltip>
                <Tooltip title={canMergeCells ? '' : mergeHint} placement="left">
                  <span style={{ display: 'block', width: '100%' }}>
                    <Button
                      size="small"
                      block
                      icon={<MergeCellsOutlined />}
                      disabled={!canMergeCells}
                      onClick={onMergeCells}
                    >
                      {t('table-merge-cells')}
                    </Button>
                  </span>
                </Tooltip>
              </KeepTooltip>
              {canUnmergeCells && (
                <Button
                  size="small"
                  block
                  icon={<SplitCellsOutlined />}
                  onClick={onUnmergeCells}
                >
                  {t('table-unmerge-cells')}
                </Button>
              )}
            </>
          )}
          {/* Ligne-titre d'un tableau variable : fusion auto ou non, pour
              toutes les lignes de même libellé (tableConfig.merge.overrides). */}
          {selectedLabelRow && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              {/* Le libellé en légende, le bouton court : un libellé long ne
                  déborde plus du bouton. */}
              <span style={labelRowCaptionStyle} title={selectedLabelRow.label}>
                {selectedLabelRow.kind
                  ? t(`table-merge-kind-${selectedLabelRow.kind}`)
                  : t('table-label-row-caption', { label: selectedLabelRow.label })}
              </span>
              <Tooltip title={t('table-label-row-merge-hint')}>
                <Button
                  size="small"
                  block
                  icon={selectedLabelRow.merged ? <SplitCellsOutlined /> : <MergeCellsOutlined />}
                  onClick={onToggleLabelRowMerge}
                >
                  {t(selectedLabelRow.merged ? 'table-unmerge-cells' : 'table-merge-cells')}
                </Button>
              </Tooltip>
            </div>
          )}
          {/* Groupement d'en-têtes : tables custom seulement (un tableau
              variable reçoit sa hiérarchie du back). */}
          {isCustomTable && (
            <>
              <span style={subgroupLabelStyle(getSectionAccentColor(t('table-columns')))}>{t('table-columns')}</span>
              {/* Même règle que la fusion : présent en permanence, désactivé tant
                  que la sélection d'en-têtes n'est pas groupable (≥2 colonnes
                  voisines, hors groupe existant). */}
              <Tooltip title={canGroupColumns ? '' : t('table-group-columns-hint')}>
                <span style={{ display: 'block', width: '100%' }}>
                  <Button
                    size="small"
                    block
                    icon={<GroupOutlined />}
                    disabled={!canGroupColumns}
                    onClick={onGroupColumns}
                  >
                    {t('table-group-columns')}
                  </Button>
                </span>
              </Tooltip>
              {canUngroupColumns && (
                <Button
                  size="small"
                  block
                  icon={<UngroupOutlined />}
                  onClick={onUngroupColumns}
                >
                  {t('table-ungroup-columns')}
                </Button>
              )}
            </>
          )}
        </div>
      )}
      <BoxFormControlsPortal title={t('display')}>
        <HideInFormSwitcher boxId={boxId} variant='panel' />
        <VariableSwitcher boxId={boxId} variant='panel' />
        <TableCascader
          onChange={handleChangeTable}
          selectedBoxType={selectedBox?.type}
          selectedBox={selectedBox}
        />
        {/* Tout tableau : colonnes, fusion et style des lignes ; les variantes
            en plus pour un tableau paramétrable. */}
        {boxId && (
          <Button
            size="small"
            onClick={() => openTableConfigDrawer(boxId)}
          >
            {t('table-configure')}
          </Button>
        )}
      </BoxFormControlsPortal>
      {pendingSplitChoice && (
        <Modal
          open={true}
          centered={true}
          onCancel={() => {
            if (isApplyingSplitChoice) return;
            setPendingSplitChoice(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                if (isApplyingSplitChoice) return;
                setPendingSplitChoice(null);
              }}
              disabled={isApplyingSplitChoice}
            >
              {t('cancel')}
            </Button>,
            <Button
              key="non-split"
              type="primary"
              disabled={isApplyingSplitChoice}
              onClick={async () => {
                if (!pendingSplitChoice) return;
                const choice = pendingSplitChoice;
                setPendingSplitChoice(null);
                setIsApplyingSplitChoice(true);
                try {
                  await applyTableChange(choice.tableName, choice.proposal, {
                    split: false,
                  });
                } finally {
                  setIsApplyingSplitChoice(false);
                }
              }}
            >
              {t('table-split-modal-non-split')}
            </Button>,
            <Button
              key="split"
              type="primary"
              disabled={isApplyingSplitChoice}
              onClick={async () => {
                if (!pendingSplitChoice) return;
                const choice = pendingSplitChoice;
                setPendingSplitChoice(null);
                setIsApplyingSplitChoice(true);
                try {
                  await applyTableChange(choice.tableName, choice.proposal, {
                    split: true,
                  });
                } finally {
                  setIsApplyingSplitChoice(false);
                }
              }}
            >
              {t('table-split-modal-split')}
            </Button>,
          ]}
          title={null}
        >
          {t('table-split-modal-question')}
        </Modal>
      )}
    </div>
  );
};

export default NavbarTable
